import { Check, ChevronDown, ChevronUp, Circle, Pencil } from "lucide-react";
import { useState } from "react";
import { useTranslate } from "@refinedev/core";

import { speciesIconSrc } from "../../components/AnimalOverview";
import { formatDate } from "../../i18n/format";
import type { DierAntwoorden, DierEntingen, DierRegel } from "./types";

/**
 * Eén dier in het dossier: wie het is, hoe ver het dossier is, en wat de eigenaar opgaf.
 *
 * <p><b>De voortgang staat links, de gegevens rechts</b> (mockup Maarten, 26-10-2026). Een
 * beoordelaar kijkt eerst of er iets ontbreekt en pas daarna naar de inhoud; andersom moet hij alles
 * lezen om te weten of hij alles heeft.
 *
 * <p><b>De voortgang is geteld, niet opgeslagen.</b> Een opgeslagen percentage veroudert zodra iemand
 * iets aanvult, en dan staat er een vinkje bij een dossier dat niet meer klopt.
 */

/** "4 jaar", of niets als de geboortedatum onbekend is. */
function leeftijd(geboortedatum: string | null): string | null {
  if (!geboortedatum) return null;
  const geboren = new Date(geboortedatum);
  if (Number.isNaN(geboren.getTime())) return null;
  const jaren = Math.floor((Date.now() - geboren.getTime()) / (365.25 * 24 * 3600 * 1000));
  return jaren < 1 ? "< 1 jaar" : `${jaren} jaar`;
}

/** De velden die een dier hoort te hebben voordat het asiel ermee verder kan. */
const BASISVELDEN: (keyof DierRegel)[] = ["naam", "diersoort", "geslacht", "geboortedatum"];

export function WachtlijstDierKaart({ dier, antwoorden, entingen, fotos, locale, aantalDieren }: {
  dier: DierRegel;
  antwoorden?: DierAntwoorden;
  entingen?: DierEntingen;
  /** De foto's van dit dier, als bron-url; leeg wanneer de eigenaar er geen stuurde. */
  fotos: { id: string; naam: string; src: string }[];
  locale: string;
  aantalDieren: number;
}) {
  const t = useTranslate();
  // Bij één dier is inklappen alleen maar een extra klik; bij meer is het juist de bedoeling.
  const [open, setOpen] = useState(aantalDieren === 1);

  const basisGevuld = BASISVELDEN.filter((v) => String(dier[v] ?? "").trim() !== "").length;
  const verplichteVragen = (antwoorden?.antwoorden ?? []).filter((a) => a.verplicht);
  const beantwoord = verplichteVragen.filter((a) => a.waarde?.trim()).length;
  const heeftEntingen = (entingen?.regels.length ?? 0) > 0;

  return (
    <section className="wl-dierkaart">
      <header className="wl-dierkaart__kop">
        <img className="wl-dierkaart__foto"
             src={fotos[0]?.src ?? speciesIconSrc(dier.diersoort)}
             alt="" />
        <div className="wl-dierkaart__naam">
          <h3>{dier.naam ?? t("wachtlijstDossier.dierZonderNaam")}</h3>
          <p>{[dier.diersoort, dier.geslacht, leeftijd(dier.geboortedatum)].filter(Boolean).join(" · ")}</p>
          <p className="fawkes-muted">
            {t("wachtlijstDossier.chipnummer")}: {dier.chipPasnummer?.trim() || "—"}
          </p>
        </div>
        <button type="button" className="wl-dierkaart__uitklap" aria-expanded={open}
                onClick={() => setOpen(!open)}>
          {open ? <ChevronUp size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
          {open ? t("wachtlijstDossier.inklappen") : t("wachtlijstDossier.uitklappen")}
        </button>
      </header>

      {open && (
        <div className="wl-dierkaart__inhoud">
          <ol className="wl-voortgang">
            <Stap klaar={basisGevuld === BASISVELDEN.length}
                  titel={t("wachtlijstDossier.stap.algemeen")}
                  stand={basisGevuld === BASISVELDEN.length
                    ? t("wachtlijstDossier.ingevuld")
                    : `${basisGevuld}/${BASISVELDEN.length}`} />
            <Stap klaar={verplichteVragen.length > 0 && beantwoord === verplichteVragen.length}
                  titel={t("wachtlijstDossier.stap.vragen")}
                  stand={verplichteVragen.length === 0
                    ? t("wachtlijstDossier.geenVragen")
                    : `${t("wachtlijstDossier.ingevuld")} (${beantwoord}/${verplichteVragen.length})`} />
            <Stap klaar={heeftEntingen}
                  titel={t("wachtlijstDossier.stap.gezondheid")}
                  stand={heeftEntingen
                    ? t("wachtlijstDossier.ingevuld")
                    : t("wachtlijstDossier.nietOpgegeven")} />
            <Stap klaar={fotos.length > 0}
                  titel={t("wachtlijstDossier.stap.extra")}
                  stand={fotos.length > 0
                    ? t("wachtlijstDossier.aantalFotos", { args: [String(fotos.length)] })
                    : t("wachtlijstDossier.optioneel")} />
          </ol>

          <dl className="wl-gegevens wl-dierkaart__gegevens">
            <Gegeven label={t("wachtlijstDossier.geboortedatum")}
                     waarde={dier.geboortedatum
                       ? `${formatDate(dier.geboortedatum, locale)}${leeftijd(dier.geboortedatum) ? ` (${leeftijd(dier.geboortedatum)})` : ""}`
                       : null} />
            <Gegeven label={t("wachtlijstDossier.ras")} waarde={dier.ras} />
            <Gegeven label={t("wachtlijstDossier.geslacht")} waarde={dier.geslacht} />
            {/* Karakter en bijzonderheden komen uit de vragenlijst; staan die er niet in, dan blijft
                de regel weg in plaats van een streepje te tonen voor iets wat nooit gevraagd is. */}
            {antwoordRegel(antwoorden, ["karakter", "KARAKTER"], t("wachtlijstDossier.karakter"))}
            {antwoordRegel(antwoorden, ["bijzonderheden", "BIJZONDERHEDEN"],
              t("wachtlijstDossier.bijzonderheden"))}
          </dl>

          <div className="wl-dierkaart__fotos">
            <h4>
              {t("wachtlijstDossier.fotos")}
              {fotos.length > 0 && <span className="wl-kaart__telling">{fotos.length}</span>}
            </h4>
            {fotos.length === 0 ? (
              <p className="fawkes-muted">{t("wachtlijstDossier.geenFotos")}</p>
            ) : (
              <ul>
                {fotos.map((f) => (
                  <li key={f.id}>
                    <img src={f.src} alt={f.naam} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * Eén regel uit de antwoorden, als de vragenlijst die vraag kent.
 *
 * <p>Op veldsleutel en niet op positie: welke vraag "het karakter" is kan de vragenlijst zelf niet
 * zeggen, hij kent alleen vragen. Kent hij de sleutel niet, dan blijft de regel weg.
 */
function antwoordRegel(antwoorden: DierAntwoorden | undefined, sleutels: string[], label: string) {
  const gevonden = (antwoorden?.antwoorden ?? []).find((a) => sleutels.includes(a.veldSleutel));
  if (!gevonden?.waarde?.trim()) {
    return null;
  }
  return <Gegeven label={label} waarde={gevonden.waarde} />;
}

function Stap({ klaar, titel, stand }: { klaar: boolean; titel: string; stand: string }) {
  return (
    <li className={klaar ? "is-klaar" : undefined}>
      <span className="wl-voortgang__bol" aria-hidden="true">
        {klaar ? <Check size={13} /> : <Circle size={13} />}
      </span>
      <span className="wl-voortgang__tekst">
        <strong>{titel}</strong>
        <small>{stand}</small>
      </span>
    </li>
  );
}

function Gegeven({ label, waarde }: { label: string; waarde: string | null }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{waarde?.trim() ? waarde : "—"}</dd>
    </div>
  );
}

/** Het potlood dat het dier opent in de inboekwizard; wordt later aangesloten. */
export function DierBewerken({ onBewerk }: { onBewerk: () => void }) {
  const t = useTranslate();
  return (
    <button type="button" className="wl-klant__knop" onClick={onBewerk}>
      <Pencil size={14} aria-hidden="true" />
      {t("wachtlijstDossier.bewerken")}
    </button>
  );
}
